import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import FactoryScene from "./components/Factory/FactoryScene";
import PinTerminal from "./components/PinTerminal/PinTerminal";
import ParticleCanvas from "./components/Effects/ParticleCanvas";
import { TEXTS } from "./config/texts";
import { useAudio } from "./hooks/useAudio";
import { useParallax } from "./hooks/useParallax";
import { useVibration } from "./hooks/useVibration";

export default function App() {
    const [phase, setPhase] = useState("locked");
    const [stage, setStage] = useState(0);
    const [status, setStatus] = useState("");
    const [heartbeatToken, setHeartbeatToken] = useState(0);
    const [finaleToken, setFinaleToken] = useState(0);
    const rootRef = useRef(null);
    const timers = useRef([]);
    const audio = useAudio(false);
    const vibrate = useVibration();

    useParallax(rootRef, 7);

    const clearTimers = useCallback(() => {
        timers.current.forEach((id) => clearTimeout(id));
        timers.current = [];
    }, []);

    const later = useCallback((delay, fn) => {
        const id = setTimeout(fn, delay);
        timers.current.push(id);
        return id;
    }, []);

    useEffect(() => {
        if (phase !== "active") return undefined;

        let interval = null;
        const first = setTimeout(() => {
            setHeartbeatToken((value) => value + 1);
            audio.playHeartbeat();
            interval = setInterval(() => {
                setHeartbeatToken((value) => value + 1);
                audio.playHeartbeat();
            }, 7200);
        }, 5200);

        return () => {
            clearTimeout(first);
            if (interval) clearInterval(interval);
        };
    }, [audio, phase]);

    useEffect(() => () => {
        clearTimers();
        audio.stopHum();
    }, [audio, clearTimers]);

    const activate = useCallback(() => {
        clearTimers();
        setPhase("blackout");
        setStage(0);
        setStatus("");
        audio.playPowerDrop();
        vibrate([28, 55, 90]);

        later(560, () => {
            setPhase("activating");
            setStage(1);
            setStatus(TEXTS.wakingGrid);
            audio.playStartup();
        });

        later(1450, () => {
            setStage(2);
            setStatus(TEXTS.powerGridOnline);
            audio.playPowerWave();
            vibrate([22, 45, 55]);
        });

        later(2600, () => {
            setStage(3);
            setStatus(TEXTS.lightingOnline);
            audio.playLightingSequence();
        });

        later(3900, () => {
            setStage(4);
            setStatus(TEXTS.pressureOnline);
            audio.playMechanics();
        });

        later(5200, () => {
            setStage(5);
            setStatus(TEXTS.heartLineOnline);
            audio.playCoreIgnition();
            vibrate([42, 72, 34, 65, 95]);
        });

        later(6550, () => {
            setStage(6);
            setStatus(TEXTS.productionOnline);
            audio.playProduction();
        });

        later(7900, () => {
            setStage(7);
            setStatus(TEXTS.synchronized);
            setFinaleToken((value) => value + 1);
            setHeartbeatToken((value) => value + 1);
            audio.playFinalSync();
            vibrate([65, 70, 120]);
        });

        later(9300, () => {
            setStage(8);
            setPhase("active");
            setStatus(TEXTS.facilityOnline);
            audio.startHum();
        });

        later(11600, () => setStatus(""));
    }, [audio, clearTimers, later, vibrate]);

    return (
        <main ref={rootRef} className={`app app--${phase}`}>
            <FactoryScene
                phase={phase}
                stage={stage}
                heartbeatToken={heartbeatToken}
                finaleToken={finaleToken}
            />
            <ParticleCanvas
                active={stage >= 4 || phase === "active"}
                stage={stage}
                heartbeatToken={heartbeatToken}
                finaleToken={finaleToken}
            />

            {phase === "locked" && (
                <div className="app__terminal-layer">
                    <PinTerminal active onSuccess={activate} audio={audio} vibrate={vibrate} />
                </div>
            )}

            {phase === "blackout" && <div className="app__blackout" aria-hidden="true" />}

            {(phase === "activating" || phase === "active") && status && (
                <div className="app__boot-status" key={status}>{status}</div>
            )}
        </main>
    );
}
